/* eslint-disable no-empty */
import { registerView } from '../viewRegistry';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import { renderBlobBackground } from '../renderer/plasma';
import { interactiveRegistry, drawInteractiveRect } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { getAddress } from 'ethers';
// import { IDENTITY_TYPE } from '../auth/universalAuth';
import { getCurrentUserSync } from '../auth/authService';
import { PLATFORM, detectPlatform } from '../device';
// Removed ensureValidAuthentication import - use isAuthenticated from auth module instead
import { isMobileDevice } from '../device.js';

// Local state to avoid repeated auth checks per frame
const confirmState = {
  authChecked: false,
  leaderboardEnabledAt: null,
  miniAppCheckDone: false,
};

function abbreviateAddress(addr) {
  if (!addr) return '';
  let checksum = addr;
  try {
    checksum = getAddress(addr); // apply EIP-55 checksum casing
  } catch { }
  if (checksum.length <= 10) return checksum;
  return `${checksum.slice(0, 6)}...${checksum.slice(-4)}`;
}

// Leaderboard icon now rendered via vector rectangles (bars 1-3-2 horizontally)
function drawLeaderboardIcon(ctx, safeArea, onClickCb) {
  const isMobile = (() => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
      ('ontouchstart' in window) ||
      (navigator.maxTouchPoints || 0) > 0;
  })();
  const dynamicSize = Math.round(safeArea.width * 0.12);
  const BOX_PX = isMobile
    ? Math.min(Math.max(dynamicSize, 64), 180)
    : 56;  // desktop size reduced back for balanced look
  const MARGIN_PX = 12; // More inward margin from edges

  let boxX = Math.round(safeArea.left + safeArea.width - BOX_PX - MARGIN_PX);
  let boxY = Math.round(safeArea.top + MARGIN_PX);

  // Draw clickable box background + border (CRT-green)
  drawInteractiveRect(ctx, { x: boxX, y: boxY, w: BOX_PX, h: BOX_PX });

  // ─── Vector bars (1-3-2) ───────────────────────────────
  ctx.save();
  ctx.fillStyle = '#39FF14';

  const BAR_WIDTH = Math.round(BOX_PX / 10);      // proportional bars
  const GAP = Math.round(BAR_WIDTH * 0.66);
  // Heights chosen to be visually distinct yet fit within the 42×42 box
  const barHeights = [Math.round(BOX_PX * 0.33), Math.round(BOX_PX * 0.80), Math.round(BOX_PX * 0.55)];

  const totalWidth = barHeights.length * BAR_WIDTH + (barHeights.length - 1) * GAP;
  const startX = boxX + Math.round((BOX_PX - totalWidth) / 2);
  const verticalPadding = Math.round(BOX_PX * 0.14); // proportional padding
  const bottomY = boxY + BOX_PX - verticalPadding;

  barHeights.forEach((h, idx) => {
    const x = startX + idx * (BAR_WIDTH + GAP);
    const y = bottomY - h;
    ctx.fillRect(x, y, BAR_WIDTH, h);
  });
  ctx.restore();

  const extraHit = isMobile ? 8 : 4; // add generous padding
  const safeCenterX = safeArea.left + safeArea.width / 2;
  const safeCenterY = safeArea.top + safeArea.height / 2;
  const iconCenterX = boxX + BOX_PX / 2;
  const iconCenterY = boxY + BOX_PX / 2;

  const normX = (iconCenterX - safeCenterX) / (safeArea.width / 2);
  const normY = (iconCenterY - safeCenterY) / (safeArea.height / 2);

  const curveCompX = Math.round(BOX_PX * 0.26 * Math.abs(normX));
  const curveCompY = Math.round(BOX_PX * 0.14 * Math.abs(normY));

  const shiftX = curveCompX * Math.sign(normX);
  const shiftY = curveCompY * Math.sign(normY);

  const el = interactiveRegistry.register({
    id: 'leaderboard-icon',
    x: boxX - extraHit - shiftX,
    y: boxY - extraHit - shiftY,
    w: BOX_PX + extraHit * 2 + Math.abs(shiftX),
    h: BOX_PX + extraHit * 2 + Math.abs(shiftY),
    onClick: () => { if (typeof onClickCb === 'function') onClickCb(); },
    focusable: true,
    skipInflation: true,
  });

  const safeRight = safeArea.left + safeArea.width;
  const safeBottom = safeArea.top + safeArea.height;
  const overflowX = (el.x + el.w) - safeRight;
  const overflowY = (el.y + el.h) - safeBottom;
  if (overflowX > 0) el.x -= overflowX;
  if (overflowY > 0) el.y -= overflowY;
}

function renderConfirm(ctx, t, w, h, params = {}) {
  const { address, ens, onTalkClick, onLoginRedirect, gasless } = params;

  // No longer needed - DopeOS manages session state centrally
  // if (!confirmState.authChecked) {
  //   confirmState.authChecked = true;
  //   (async () => {
  //     const authRes = await ensureValidAuthentication({ silentFail: true });
  //     if (!authRes.success && typeof onLoginRedirect === 'function') onLoginRedirect();
  //   })();
  // }
  // if (!isAuthenticated()) { if (typeof onLoginRedirect === 'function') { onLoginRedirect(); return; } }

  renderBlobBackground(ctx, t, w, h);
  if (!address || !DopeTextRenderer.isReady()) return;

  const isActualMobile = (() => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
      ('ontouchstart' in window) ||
      (navigator.maxTouchPoints || 0) > 0;
  })();

  const CELL = 16;
  const isMiniCtx = typeof detectPlatform === 'function' && detectPlatform() === PLATFORM.FARCASTER;
  const textScale = (isActualMobile || isMiniCtx) ? 4.7 : 1.6;

  const buffer = { w, h };
  const safeArea = getCanonicalSafeArea(buffer);

  if (!confirmState.leaderboardEnabledAt) confirmState.leaderboardEnabledAt = Date.now() + 300;
  if (Date.now() >= confirmState.leaderboardEnabledAt) drawLeaderboardIcon(ctx, safeArea, params.onLeaderboardClick);

  const user = getCurrentUserSync();
  // CRITICAL FIX: user object contains fields directly (not in .identity)
  // getCurrentUserSync returns { address, fid, username, displayName, platform, ... }

  // DEBUG: Log user object for Farcaster troubleshooting
  if (user && user.platform === PLATFORM.FARCASTER && !confirmState.userLogged) {
    console.log('[Confirmation] 🎯 Farcaster user data:', {
      hasFid: !!user.fid,
      fid: user.fid,
      hasUsername: !!user.username,
      username: user.username,
      usernameType: typeof user.username,
      hasDisplayName: !!user.displayName,
      displayName: user.displayName,
      address: user.address,
      platform: user.platform
    });
    confirmState.userLogged = true; // Log only once
  }

  const lines = [];
  const addressesShown = new Set();

  // Ensure all user properties are primitives to prevent "Cannot convert object to primitive value" errors
  const safeStr = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'string' || typeof val === 'number') return String(val);
    if (typeof val === 'object' && val.value) return String(val.value); // Handle { value: "..." } objects
    return null; // Don't use objects in template strings
  };

  if (user) {
    switch (user.platform) {
      case PLATFORM.FARCASTER: lines.push('FARCASTER ID LINKED!'); break;
      case PLATFORM.WORLDCOIN: lines.push('WORLDCOIN ID LINKED!'); break;
      case PLATFORM.COINBASE: lines.push('BASE WALLET LINKED!'); break;
      default: lines.push('WALLET LINKED!'); break;
    }
    if (user.platform === PLATFORM.FARCASTER) {
      // PRIORITY 1: Show username if available (much better UX)
      const username = safeStr(user.username);
      if (username) {
        lines.push(`@${username}`);
      }
      // PRIORITY 2: Show wallet address if it's a real 0x address
      const userAddr = safeStr(user.address);
      if (userAddr && userAddr.startsWith('0x')) {
        const addrShort = abbreviateAddress(userAddr);
        if (!addressesShown.has(addrShort.toLowerCase())) { lines.push(addrShort); addressesShown.add(addrShort.toLowerCase()); }
      }
      // PRIORITY 3: Show FID only if no username (fallback)
      if (!username && user.fid) {
        const fidStr = safeStr(user.fid);
        if (fidStr) lines.push(`FID: ${fidStr}`);
      }
    } else if (user.platform === PLATFORM.WORLDCOIN) {
      const worldId = safeStr(user.worldId);
      lines.push(`WorldID: ${worldId || 'Connected'}`);
      const verificationLevel = safeStr(user.verificationLevel);
      if (verificationLevel) lines.push(`Verified: ${verificationLevel}`);
    } else {
      const ensName = safeStr(user.ensName);
      if (ensName) lines.push(ensName);
      else {
        const userAddr = safeStr(user.address);
        if (userAddr) {
          const addrShort = abbreviateAddress(userAddr);
          if (!addressesShown.has(addrShort.toLowerCase())) { lines.push(addrShort); addressesShown.add(addrShort.toLowerCase()); }
        }
      }
    }
  } else {
    lines.push('WALLET LINKED!');
    const ensStr = safeStr(ens);
    const addrStr = safeStr(address);
    if (ensStr) lines.push(ensStr);
    else if (addrStr) {
      const addrShortFallback = abbreviateAddress(addrStr);
      if (!addressesShown.has(addrShortFallback.toLowerCase())) { lines.push(addrShortFallback); addressesShown.add(addrShortFallback.toLowerCase()); }
    }
  }
  if (gasless) lines.push('GASLESS OK');

  const seen = new Set();
  const dedupedLines = lines.filter(l => { const k = l.toLowerCase().trim(); if (seen.has(k)) return false; seen.add(k); return true; });
  const widths = dedupedLines.map(txt => DopeTextRenderer.measureDOSText(txt, textScale).width);
  const maxLineW = Math.max(...widths);

  const lineHeight = CELL * textScale;
  const padding = 8 * textScale;
  const blockHpx = dedupedLines.length * lineHeight + padding * 2;
  const blockWpx = maxLineW + padding * 2;

  const centerX = safeArea.left + safeArea.width / 2;
  const centerY = safeArea.top + safeArea.height / 2;

  const blockX = centerX - blockWpx / 2;
  const blockY = centerY - blockHpx / 2;

  ctx.fillStyle = '#000'; ctx.globalAlpha = 0.9; ctx.fillRect(blockX, blockY, blockWpx, blockHpx); ctx.globalAlpha = 1.0;

  ctx.fillStyle = '#39FF14';
  let yPtr = blockY + padding;
  dedupedLines.forEach((txt, idx) => {
    const yLine = yPtr + idx * lineHeight;
    const txtMeasure = DopeTextRenderer.measureDOSText(txt, textScale);
    DopeTextRenderer.renderDOSText(ctx, txt, centerX - txtMeasure.width / 2, yLine, textScale);
  });

  const bottomBlockPixelY = blockY + blockHpx;
  const labels = ['TALK TO A SHARK', 'ABOUT DCW', 'DISCONNECT WALLET'];
  const btnTextScale = (isActualMobile || isMiniCtx) ? 4.2 : 1.3;
  const meas = labels.map(lbl => DopeTextRenderer.measureDOSText(lbl, btnTextScale));
  const btnPadX = (isActualMobile || isMiniCtx) ? 16 : 12;
  const btnPadY = (isActualMobile || isMiniCtx) ? 12 : 10;
  const rawWidths = meas.map(m => m.width + btnPadX * 2);
  const widestRaw = Math.max(...rawWidths);
  const maxMobileWidth = Math.floor(safeArea.width * 0.9);
  const btnWidth = (isActualMobile || isMiniCtx) ? Math.min(widestRaw, maxMobileWidth) : widestRaw;
  const btnHeight = meas[0].height + btnPadY * 2;
  const gapY = (isActualMobile || isMiniCtx) ? 36 : 12;
  const totalButtonsHeight = btnHeight * 3 + gapY * 2;
  const firstBtnYDefault = bottomBlockPixelY + 24;
  let firstBtnY = firstBtnYDefault;
  const maxFirstY = safeArea.top + safeArea.height - totalButtonsHeight - 8;
  if (firstBtnY > maxFirstY) firstBtnY = maxFirstY;
  const btnX = Math.round(safeArea.left + (safeArea.width - btnWidth) / 2);

  function drawButton(idx, label, onClick) {
    const y = firstBtnY + idx * (btnHeight + gapY);
    ctx.save(); ctx.fillStyle = '#000'; ctx.globalAlpha = 1.0; ctx.fillRect(btnX, y, btnWidth, btnHeight); ctx.strokeStyle = '#39FF14'; ctx.lineWidth = 3; ctx.strokeRect(btnX + 1, y + 1, btnWidth - 2, btnHeight - 2); ctx.restore();
    interactiveRegistry.register({ id: `${label.replace(/\s+/g, '-').toLowerCase()}-btn`, x: btnX, y, w: btnWidth, h: btnHeight, onClick, focusable: true });
    const textMeasure = DopeTextRenderer.measureDOSText(label, btnTextScale);
    const textX = Math.round(btnX + (btnWidth - textMeasure.width) / 2);
    const baselineAdj = Math.round(textMeasure.height * 0.2);
    const textY = y + Math.round((btnHeight - textMeasure.height) / 2) + baselineAdj;
    DopeTextRenderer.renderDOSText(ctx, label, textX, textY, btnTextScale);
  }

  drawButton(0, labels[0], () => { if (typeof onTalkClick === 'function') onTalkClick(); });
  drawButton(1, labels[1], () => { if (typeof params.onAboutClick === 'function') params.onAboutClick(); else if (typeof window !== 'undefined' && window.DCW_showView) window.DCW_showView('about'); });
  drawButton(2, labels[2], () => { if (typeof params.onDisconnectClick === 'function') params.onDisconnectClick(); });
}

registerView('confirmation', renderConfirm); 